import { useState, useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import { llmModelService } from '../../services/llmModelService';
import { LlmModelModal } from '../../components/admin/LlmModelModal';
import { FilterableTable } from '../../components/ui';
import type { FilterableColumnDef } from '../../components/ui';
import type { LLMModel } from '../../types/llmModel';
import { PageContainer } from '../../components/layout';
import { UsagesModal } from '../../components/catalog/UsagesModal';
import { useCatalogUsages } from '../../components/catalog/useCatalogUsages';

export function LlmModelsPage() {
  const [models, setModels] = useState<LLMModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingModel, setEditingModel] = useState<LLMModel | null>(null);

  const usages = useCatalogUsages('llm-models');

  useEffect(() => {
    loadModels();
  }, []);

  const loadModels = async () => {
    try {
      setLoading(true);
      const data = await llmModelService.getAll();
      setModels(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (model: LLMModel) => {
    setEditingModel(model);
    setShowModal(true);
  };

  const handleDelete = async (model: LLMModel) => {
    if (model.usage_count > 0) {
      toast.error(`Ce modèle est référencé par ${model.usage_count} config(s) LLM du catalogue`);
      return;
    }
    if (!confirm(`Supprimer le modèle "${model.display_name}" ?`)) return;
    try {
      await llmModelService.delete(model.id);
      toast.success('Modèle supprimé');
      loadModels();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erreur de suppression');
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingModel(null);
  };

  const formatPrice = (price: number | string) => {
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    return `$${numPrice.toFixed(2)}`;
  };

  const formatNumber = (num: number | null) => {
    if (num === null) return '-';
    return num.toLocaleString();
  };

  const columns: FilterableColumnDef<LLMModel>[] = useMemo(() => [
    {
      key: 'display_name',
      header: 'Modèle',
      sortable: true,
      searchable: true,
      render: (m) => (
        <div>
          <div className="font-medium text-gray-900">{m.display_name}</div>
          <div className="text-sm text-gray-500 font-mono">{m.model_name}</div>
        </div>
      ),
    },
    {
      key: 'provider',
      header: 'Provider',
      filterable: true,
      render: (m) => (
        <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded ${
          m.provider === 'openai' ? 'bg-green-100 text-green-800'
            : m.provider === 'anthropic' ? 'bg-orange-100 text-orange-800'
            : 'bg-gray-100 text-gray-800'
        }`}>
          {m.provider}
        </span>
      ),
    },
    {
      key: 'price_input',
      header: 'Prix Input (1M)',
      align: 'right' as const,
      sortable: true,
      sortValueFn: (m) => String(typeof m.price_input === 'string' ? parseFloat(m.price_input) : m.price_input).padStart(10, '0'),
      render: (m) => <span className="font-mono text-sm">{formatPrice(m.price_input)}</span>,
    },
    {
      key: 'price_output',
      header: 'Prix Output (1M)',
      align: 'right' as const,
      sortable: true,
      sortValueFn: (m) => String(typeof m.price_output === 'string' ? parseFloat(m.price_output) : m.price_output).padStart(10, '0'),
      render: (m) => <span className="font-mono text-sm">{formatPrice(m.price_output)}</span>,
    },
    {
      key: 'context_window',
      header: 'Context Window',
      align: 'right' as const,
      sortable: true,
      sortValueFn: (m) => String(m.context_window ?? 0).padStart(10, '0'),
      render: (m) => <span className="font-mono text-sm">{formatNumber(m.context_window)}</span>,
    },
    {
      key: 'capabilities',
      header: 'Capacités',
      align: 'center' as const,
      render: (m) => (
        <div className="flex justify-center gap-1">
          {m.supports_json_mode && (
            <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded" title="JSON Mode">JSON</span>
          )}
          {m.supports_vision && (
            <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-800 rounded" title="Vision">Vision</span>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Statut',
      align: 'center' as const,
      filterable: true,
      filterValueFn: (m) => m.is_active ? 'actif' : 'inactif',
      filterOptions: [
        { value: 'actif', label: 'Actif' },
        { value: 'inactif', label: 'Inactif' },
      ],
      render: (m) => m.is_active ? (
        <span className="px-2 py-0.5 text-xs font-medium rounded bg-green-100 text-green-800">Actif</span>
      ) : (
        <span className="px-2 py-0.5 text-xs font-medium rounded bg-gray-100 text-gray-800">Inactif</span>
      ),
    },
    {
      key: 'tenants_count',
      header: 'Activé dans',
      align: 'center' as const,
      sortable: true,
      sortValueFn: (m) => String(m.tenants_count ?? 0).padStart(6, '0'),
      render: (m) => (
        <button
          onClick={() => usages.openModal(m.id, m.display_name)}
          className="text-blue-600 hover:underline text-sm"
          title="Voir les tenants où ce modèle est activé"
        >
          {m.tenants_count ?? 0} tenants
        </button>
      ),
    },
    {
      key: 'usage_count',
      header: 'Configs',
      align: 'center' as const,
      sortable: true,
      sortValueFn: (m) => String(m.usage_count).padStart(6, '0'),
      render: (m) => (
        <span className={`px-2 py-0.5 text-xs font-medium rounded ${
          m.usage_count > 0 ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-500'
        }`}>
          {m.usage_count}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right' as const,
      render: (m) => (
        <div className="flex justify-end gap-2">
          <button onClick={() => handleEdit(m)} className="text-primary hover:text-primary-600" title="Modifier">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button
            onClick={() => handleDelete(m)}
            disabled={m.usage_count > 0}
            className={m.usage_count > 0 ? 'text-gray-300 cursor-not-allowed' : 'text-red-500 hover:text-red-700'}
            title={m.usage_count > 0 ? 'Modèle référencé par une config LLM du catalogue' : 'Supprimer'}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      ),
    },
  ], [usages]);

  if (error) {
    return (
      <PageContainer>
        <div className="bg-red-50 border border-red-200 rounded p-4">
          <p className="text-red-800">{error}</p>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Modèles LLM</h1>
          <p className="text-gray-600 mt-1 text-sm">Catalogue des modèles disponibles avec tarification</p>
        </div>
        <button
          onClick={() => { setEditingModel(null); setShowModal(true); }}
          className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-600"
        >
          + Nouveau modèle
        </button>
      </div>

      {loading ? (
        <div>Chargement...</div>
      ) : (
        <FilterableTable
          persistKey="admin-llm-models"
          paginated
          data={models}
          columns={columns}
          rowKey={(m) => m.id}
          defaultSort={{ key: 'display_name', direction: 'asc' }}
          rowClassName={(m) => !m.is_active ? 'opacity-60' : ''}
          noDataMessage="Aucun modèle LLM configuré"
          emptyMessage="Aucun modèle ne correspond aux filtres"
        />
      )}

      <LlmModelModal
        isOpen={showModal}
        onClose={handleModalClose}
        onSuccess={() => { handleModalClose(); loadModels(); }}
        model={editingModel}
      />

      <UsagesModal
        open={usages.modal.open}
        catalogType="llm-models"
        objectId={usages.modal.objectId}
        objectLabel={usages.modal.objectLabel}
        onClose={usages.closeModal}
      />
    </PageContainer>
  );
}
